import { useState, useMemo } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Users, Shield, AlertTriangle, Clock, Search, GraduationCap, CheckCircle2, Ship, Calendar } from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";

const EmployeeCrewPersonnel = () => {
  const [search, setSearch] = useState("");

  const { data: crewSchedules = [], isLoading: loadingCrew } = useQuery({
    queryKey: ["crew-schedules-all"],
    queryFn: async () => {
      const { data } = await supabase.from("crew_schedules").select("*").order("rotation_start", { ascending: false });
      return data ?? [];
    },
  });

  const { data: training = [], isLoading: loadingTraining } = useQuery({
    queryKey: ["training-records-all"],
    queryFn: async () => {
      const { data } = await supabase.from("training_records").select("*").order("expiry_date");
      return data ?? [];
    },
  });

  const expiringCerts = useMemo(() =>
    training.filter(t => t.expiry_date && differenceInDays(parseISO(t.expiry_date), new Date()) <= 90 && differenceInDays(parseISO(t.expiry_date), new Date()) > 0),
    [training]
  );
  const expiredCerts = useMemo(() =>
    training.filter(t => t.expiry_date && differenceInDays(parseISO(t.expiry_date), new Date()) <= 0),
    [training]
  );

  const onboard = crewSchedules.filter(c => c.status === "onboard");
  const filteredCrew = crewSchedules.filter(c =>
    c.crew_member_name.toLowerCase().includes(search.toLowerCase()) ||
    c.vessel_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <IntranetLayout>
      <div className="mb-6">
        <h1 className="font-display text-2xl text-foreground">Crew & Personnel</h1>
        <p className="text-sm text-muted-foreground font-body mt-1">Manage crew rotations, certifications, and personnel records</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Crew Onboard", value: onboard.length.toString(), icon: Users, color: "text-teal" },
          { label: "Total Certs", value: training.length.toString(), icon: GraduationCap, color: "text-lime-dark" },
          { label: "Expiring (<90d)", value: expiringCerts.length.toString(), icon: Clock, color: "text-amber-500" },
          { label: "Expired", value: expiredCerts.length.toString(), icon: AlertTriangle, color: "text-red-500" },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <k.icon className={`w-8 h-8 ${k.color}`} />
              <div>
                <p className="text-xl font-display text-foreground">{k.value}</p>
                <p className="text-[11px] text-muted-foreground font-body">{k.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search crew..." className="pl-10" />
      </div>

      <Tabs defaultValue="roster">
        <TabsList>
          <TabsTrigger value="roster">Active Roster</TabsTrigger>
          <TabsTrigger value="certs">Certification Matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="mt-4">
          {loadingCrew ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
          ) : filteredCrew.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No crew records found</p></CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {filteredCrew.map(c => (
                <Card key={c.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center">
                        <span className="text-sm font-bold text-teal">{c.crew_member_name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-display text-sm text-foreground">{c.crew_member_name}</p>
                        <p className="text-xs text-muted-foreground font-body">{c.position} · {c.vessel_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${c.status === "onboard" ? "bg-emerald-100 text-emerald-700" : c.status === "on_leave" ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}>
                        {c.status.replace("_", " ")}
                      </span>
                      <p className="text-[10px] text-muted-foreground font-body mt-1">{format(parseISO(c.rotation_start), "dd MMM")} – {format(parseISO(c.rotation_end), "dd MMM yyyy")}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="certs" className="mt-4">
          {loadingTraining ? (
            <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
          ) : training.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><GraduationCap className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No certification records</p></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {training.map(t => {
                const daysLeft = t.expiry_date ? differenceInDays(parseISO(t.expiry_date), new Date()) : null;
                const urgency = daysLeft !== null ? (daysLeft <= 0 ? "bg-red-100 text-red-700" : daysLeft <= 90 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700") : "bg-muted text-muted-foreground";
                return (
                  <Card key={t.id}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-body text-sm font-medium text-foreground">{t.certification_name}</p>
                        <p className="text-xs text-muted-foreground font-body">{t.issuing_body || "—"}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${urgency}`}>
                          {daysLeft !== null ? (daysLeft <= 0 ? "EXPIRED" : `${daysLeft}d left`) : t.status}
                        </span>
                        {t.expiry_date && <p className="text-[10px] text-muted-foreground font-body mt-1">Exp: {format(parseISO(t.expiry_date), "dd MMM yyyy")}</p>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default EmployeeCrewPersonnel;
